import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
} from "recharts";
import { SKILLS, METHODOLOGIES, TOOLS, RADAR_DATA, SECTORS } from "../../data/content";
import { useInView } from "../../hooks/useInView";
import { Reveal } from "./Reveal";

const headingStyle = {
  fontSize: "var(--text-sm)", fontWeight: 700, color: "var(--text-muted)",
  textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "var(--space-4)",
};

// Static Recharts config — hoisted so a new object reference isn't created each render.
const AXIS_TICK = { fill: "#8b949e", fontSize: 11, fontFamily: "'DM Sans', sans-serif" };
const RADIUS_DOMAIN = [50, 100];
const RADAR_DOT = { r: 3, fill: "#1A8C79" };

export const Expertise = () => {
  const [skillRef, skillsInView] = useInView({ threshold: 0.2 });

  return (
    <section className="section" id="expertise" aria-labelledby="expertise-heading">
      <div className="container">
        <div className="section-header">
          <div className="section-label">Technical Depth</div>
          <h2 className="section-heading" id="expertise-heading">Methodological <em>Expertise</em></h2>
          <p className="section-sub">
            Deep proficiency across the full spectrum of evaluation methodologies, M&amp;E frameworks, and
            data systems — applied across conflict-affected, fragile, and complex operating environments.
          </p>
        </div>

        <div className="expertise-layout">
          <div>
            <div className="skill-bars" ref={skillRef}>
              {SKILLS.map((s, i) => (
                <div className="skill-row" key={s.name}>
                  <div className="skill-header">
                    <span className="skill-name">{s.name}</span>
                    <span className="skill-pct">{s.pct}%</span>
                  </div>
                  <div className="skill-track">
                    <div className={`skill-fill ${skillsInView ? "animate" : ""}`}
                         style={{ width: `${s.pct}%`, animationDelay: `${i * 120}ms` }} />
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: "var(--space-8)" }}>
              <h3 style={headingStyle}>Evaluation Methodologies</h3>
              <div className="methodology-cloud">
                {METHODOLOGIES.map(([text, variant]) => (
                  <span key={text} className={`meth-badge meth-badge--${variant}`}>{text}</span>
                ))}
              </div>
            </div>
          </div>

          <div>
            <div className="expertise-chart-wrap">
              <h3 style={headingStyle}>Sectoral Expertise</h3>
              <div style={{ width: "100%", height: 360 }} aria-label="Radar chart of sectoral expertise">
                <ResponsiveContainer>
                  <RadarChart data={RADAR_DATA} outerRadius="72%">
                    <PolarGrid stroke="rgba(143,137,128,0.30)" />
                    <PolarAngleAxis dataKey="subject" tick={AXIS_TICK} />
                    <PolarRadiusAxis domain={RADIUS_DOMAIN} tick={false} axisLine={false} />
                    <Radar dataKey="value" stroke="#1A8C79" strokeWidth={2}
                           fill="#1A8C79" fillOpacity={0.14} dot={RADAR_DOT} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div style={{ marginTop: "var(--space-5)", background: "var(--surface)",
              border: "1px solid var(--border)", borderRadius: "var(--radius-xl)", padding: "var(--space-5)" }}>
              <h3 style={headingStyle}>Data &amp; Digital Tools</h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)" }}>
                {TOOLS.map(([text, variant]) => (
                  <span key={text} className={`meth-badge meth-badge--${variant}`}>{text}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: "var(--space-12)" }}>
          <h3 style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-xl)", color: "var(--text)", marginBottom: "var(--space-6)" }}>
            Sectoral <em style={{ fontStyle: "italic", color: "var(--primary)" }}>Coverage</em>
          </h3>
          <div className="sectors-grid">
            {SECTORS.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal className="sector-card" key={s.name} delay={(i % 4) * 60}>
                  <div className="sector-icon"><Icon size={20} /></div>
                  <div>
                    <div className="sector-name">{s.name}</div>
                    <div className="sector-sub">{s.sub}</div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
